import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Avatar,
  IconButton,
  Collapse,
  InputAdornment,
  Chip,
  LinearProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import TodayIcon from "@mui/icons-material/Today";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaymentsIcon from "@mui/icons-material/Payments";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import MuiPageHeader from "../../components/mui/MuiPageHeader";
import { fetchPaymentsDate } from "../../helpers/api_helper";

const formatDate = (date, locale = "en-US") => {
  const d = new Date(date);
  return d.toLocaleDateString(locale);
};

const getYearStart = () => {
  const now = new Date();
  return `${now.getFullYear()}-01-01`;
};

const getToday = () => new Date().toISOString().split("T")[0];

const MuiCashRegister = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "el" ? "el-GR" : "en-US";

  document.title = t("Cash Register") + " | FlexFlow ERP";

  const [fromDate, setFromDate] = useState(getToday());
  const [tillDate, setTillDate] = useState(getToday());
  const [allYearPayments, setAllYearPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(false);

  const [rangeTotals, setRangeTotals] = useState({ inbound: 0, outbound: 0 });
  const [progressiveTotals, setProgressiveTotals] = useState({ inbound: 0, outbound: 0 });
  const [beforePeriodTotals, setBeforePeriodTotals] = useState({ inbound: 0, outbound: 0 });
  const [groupedTypeTotals, setGroupedTypeTotals] = useState({});

  const [showPayments, setShowPayments] = useState(false);
  const [searchText, setSearchText] = useState("");

  const calculateTotals = (payments) => {
    let inbound = 0;
    let outbound = 0;
    payments.forEach(({ bound, ammount }) => {
      const amt = parseFloat(ammount || 0);
      if (bound === "Inbound") inbound += amt;
      else if (bound === "Outbound") outbound += amt;
    });
    return { inbound, outbound };
  };

  const totalBalance = (totals) => (totals.inbound - totals.outbound).toFixed(2);

  const filterByType = (payments, type) => payments.filter((p) => p.type === type);

  const fetchAllData = async () => {
    if (!fromDate || !tillDate) {
      return;
    }

    setLoading(true);
    try {
      const yearStart = getYearStart();
      const all = await fetchPaymentsDate(yearStart, tillDate);
      setAllYearPayments(all || []);

      const filtered = (all || []).filter((p) => {
        const d = new Date(p.date);
        return d >= new Date(fromDate) && d <= new Date(tillDate);
      });

      const before = (all || []).filter((p) => new Date(p.date) < new Date(fromDate));

      setFilteredPayments(filtered);
      setRangeTotals(calculateTotals(filtered));
      setProgressiveTotals(calculateTotals(all || []));
      setBeforePeriodTotals(calculateTotals(before));

      const typesToTrack = ["CASH", "BANK", "CARD", "CHECK"];
      const result = {};

      typesToTrack.forEach((type) => {
        const filteredByType = filterByType(all || [], type);
        const beforeByType = filteredByType.filter((p) => new Date(p.date) < new Date(fromDate));
        const inRangeByType = filteredByType.filter(
          (p) => new Date(p.date) >= new Date(fromDate) && new Date(p.date) <= new Date(tillDate)
        );

        result[type] = {
          before: calculateTotals(beforeByType),
          range: calculateTotals(inRangeByType),
          progressive: calculateTotals(filteredByType),
        };
      });

      setGroupedTypeTotals(result);
    } catch (error) {
      console.error("Error fetching payments:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const filteredAndSearchedPayments = useMemo(() => {
    if (!searchText) return filteredPayments;
    const s = searchText.toLowerCase();
    return filteredPayments.filter(
      (p) =>
        p.entity_name?.toLowerCase().includes(s) ||
        p.refcode?.toLowerCase().includes(s) ||
        p.type?.toLowerCase().includes(s) ||
        p.bound?.toLowerCase().includes(s) ||
        p.ammount?.toString().includes(s)
    );
  }, [filteredPayments, searchText]);

  // DataGrid columns
  const columns = useMemo(
    () => [
      {
        field: "date",
        headerName: t("Date"),
        width: 120,
        renderCell: (params) => (
          <Typography variant="body2" color="#374151">
            {formatDate(params.value, locale)}
          </Typography>
        ),
      },
      {
        field: "entity_name",
        headerName: t("Name"),
        flex: 1,
        minWidth: 180,
        renderCell: (params) => (
          <Typography variant="body2" fontWeight={500} color="#111827">
            {params.value || "-"}
          </Typography>
        ),
      },
      {
        field: "refcode",
        headerName: t("Reference"),
        width: 130,
        renderCell: (params) => (
          <Chip
            label={params.value || "-"}
            size="small"
            sx={{ bgcolor: "#f3f4f6", color: "#374151", fontSize: "0.75rem" }}
          />
        ),
      },
      {
        field: "type",
        headerName: t("Type"),
        width: 100,
        renderCell: (params) => {
          const typeColors = {
            CASH: { bg: "#dcfce7", color: "#16a34a" },
            BANK: { bg: "#dbeafe", color: "#2563eb" },
            CARD: { bg: "#fef3c7", color: "#d97706" },
            CHECK: { bg: "#f3e8ff", color: "#9333ea" },
          };
          const colors = typeColors[params.value] || { bg: "#f3f4f6", color: "#6b7280" };
          return (
            <Chip
              label={params.value || "-"}
              size="small"
              sx={{ bgcolor: colors.bg, color: colors.color, fontWeight: 500, fontSize: "0.75rem" }}
            />
          );
        },
      },
      {
        field: "bound",
        headerName: t("Direction"),
        width: 110,
        renderCell: (params) => {
          const isInbound = params.value === "Inbound";
          return (
            <Chip
              label={isInbound ? t("Inbound") : t("Outbound")}
              size="small"
              sx={{
                bgcolor: isInbound ? "#dcfce7" : "#fee2e2",
                color: isInbound ? "#16a34a" : "#dc2626",
                fontWeight: 500,
                fontSize: "0.75rem",
              }}
            />
          );
        },
      },
      {
        field: "ammount",
        headerName: t("Amount"),
        width: 120,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Typography variant="body2" fontWeight={600} color="#111827">
            €{parseFloat(params.value || 0).toFixed(2)}
          </Typography>
        ),
      },
    ],
    [t, locale]
  );

  // Chart data for balance evolution
  const balanceChartData = useMemo(() => {
    if (!allYearPayments.length) return [];

    const balances = [];
    let runningInbound = 0;
    let runningOutbound = 0;

    const dayMs = 24 * 60 * 60 * 1000;
    const start = new Date(getYearStart());
    const end = new Date(tillDate);

    for (let d = new Date(start); d <= end; d = new Date(d.getTime() + dayMs)) {
      const day = d.toISOString().split("T")[0];

      const daily = allYearPayments.filter((p) => p.date?.startsWith(day));
      daily.forEach((p) => {
        const amt = parseFloat(p.ammount || 0);
        if (p.bound === "Inbound") runningInbound += amt;
        if (p.bound === "Outbound") runningOutbound += amt;
      });

      // Only add weekly data points to avoid clutter
      if (balances.length === 0 || d.getDay() === 0 || d >= end) {
        balances.push({
          date: formatDate(day, locale),
          balance: parseFloat((runningInbound - runningOutbound).toFixed(2)),
        });
      }
    }

    return balances;
  }, [allYearPayments, tillDate, locale]);

  // Chart data for payments by type
  const paymentsByTypeData = useMemo(() => {
    const map = {};
    allYearPayments.forEach(({ type, ammount, bound }) => {
      if (!type || !bound) return;
      const amt = parseFloat(ammount || 0);
      if (!map[type]) {
        map[type] = { type, inbound: 0, outbound: 0 };
      }
      if (bound === "Inbound") map[type].inbound += amt;
      if (bound === "Outbound") map[type].outbound += amt;
    });

    return Object.values(map).map((item) => ({
      ...item,
      inbound: parseFloat(item.inbound.toFixed(2)),
      outbound: parseFloat(item.outbound.toFixed(2)),
    }));
  }, [allYearPayments]);

  const handleToday = () => {
    const today = getToday();
    setFromDate(today);
    setTillDate(today);
    setTimeout(fetchAllData, 0);
  };

  const breadcrumbs = [
    { label: t("Views"), path: "/dashboard" },
    { label: t("Cash Register") },
  ];

  const typeLabels = {
    CASH: t("Cash"),
    BANK: t("Bank"),
    CARD: t("Card"),
    CHECK: t("Check"),
  };

  const typeIcons = {
    CASH: <PaymentsIcon />,
    BANK: <AccountBalanceIcon />,
    CARD: <CreditCardIcon />,
    CHECK: <ReceiptLongIcon />,
  };

  const typeColors = {
    CASH: { bg: "#dcfce7", icon: "#16a34a" },
    BANK: { bg: "#dbeafe", icon: "#2563eb" },
    CARD: { bg: "#fef3c7", icon: "#d97706" },
    CHECK: { bg: "#f3e8ff", icon: "#9333ea" },
  };

  return (
    <Box sx={{ py: 3, bgcolor: "#f9fafb", minHeight: "100vh" }}>
      <Container maxWidth={false}>
        <MuiPageHeader
          title={t("Cash Register")}
          subtitle={t("Track your cash flow and payment history")}
          breadcrumbs={breadcrumbs}
        />

        {/* Date Filter Card */}
        <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb", mb: 3 }}>
          <CardContent sx={{ py: 2 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ md: "center" }}
              justifyContent="space-between"
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                <TextField
                  type="date"
                  size="small"
                  label={t("From Date")}
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarTodayIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    minWidth: 180,
                    "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  }}
                />
                <TextField
                  type="date"
                  size="small"
                  label={t("To Date")}
                  value={tillDate}
                  onChange={(e) => setTillDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarTodayIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    minWidth: 180,
                    "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  }}
                />
              </Stack>

              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={fetchAllData}
                  disabled={loading}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    px: 3,
                    bgcolor: "#3b82f6",
                    "&:hover": { bgcolor: "#2563eb" },
                  }}
                >
                  {t("View")}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<TodayIcon />}
                  onClick={handleToday}
                  disabled={loading}
                  sx={{ borderRadius: 2, textTransform: "none" }}
                >
                  {t("Today")}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
          {loading && <LinearProgress />}
        </Card>

        {allYearPayments.length > 0 && (
          <>
            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {/* Before Period */}
              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: "1px solid #e5e7eb",
                    bgcolor: "#fffbeb",
                    height: "100%",
                  }}
                >
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                      <Avatar sx={{ bgcolor: "#fef3c7", color: "#d97706" }}>
                        <TrendingUpIcon />
                      </Avatar>
                      <Typography variant="subtitle1" fontWeight={600} color="#92400e">
                        {t("Balance Before Period")}
                      </Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="#78716c">
                          {t("Inbound")}
                        </Typography>
                        <Typography variant="h6" fontWeight={600} color="#16a34a">
                          €{beforePeriodTotals.inbound.toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="#78716c">
                          {t("Outbound")}
                        </Typography>
                        <Typography variant="h6" fontWeight={600} color="#dc2626">
                          €{beforePeriodTotals.outbound.toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ pt: 1, borderTop: "1px solid #fde68a" }}>
                          <Typography variant="caption" color="#78716c">
                            {t("Balance")}
                          </Typography>
                          <Typography variant="h5" fontWeight={700} color="#92400e">
                            €{totalBalance(beforePeriodTotals)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Selected Period */}
              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: "1px solid #e5e7eb",
                    bgcolor: "#ecfdf5",
                    height: "100%",
                  }}
                >
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                      <Avatar sx={{ bgcolor: "#dcfce7", color: "#16a34a" }}>
                        <AccountBalanceWalletIcon />
                      </Avatar>
                      <Typography variant="subtitle1" fontWeight={600} color="#166534">
                        {t("Selected Period Totals")}
                      </Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="#78716c">
                          {t("Inbound")}
                        </Typography>
                        <Typography variant="h6" fontWeight={600} color="#16a34a">
                          €{rangeTotals.inbound.toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="#78716c">
                          {t("Outbound")}
                        </Typography>
                        <Typography variant="h6" fontWeight={600} color="#dc2626">
                          €{rangeTotals.outbound.toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ pt: 1, borderTop: "1px solid #a7f3d0" }}>
                          <Typography variant="caption" color="#78716c">
                            {t("Balance")}
                          </Typography>
                          <Typography variant="h5" fontWeight={700} color="#166534">
                            €{totalBalance(rangeTotals)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Progressive Total */}
              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: "1px solid #e5e7eb",
                    bgcolor: "#eff6ff",
                    height: "100%",
                  }}
                >
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                      <Avatar sx={{ bgcolor: "#dbeafe", color: "#2563eb" }}>
                        <TrendingDownIcon />
                      </Avatar>
                      <Typography variant="subtitle1" fontWeight={600} color="#1e40af">
                        {t("Progressive Balance")} ({formatDate(tillDate, locale)})
                      </Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="#78716c">
                          {t("Inbound")}
                        </Typography>
                        <Typography variant="h6" fontWeight={600} color="#16a34a">
                          €{progressiveTotals.inbound.toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="#78716c">
                          {t("Outbound")}
                        </Typography>
                        <Typography variant="h6" fontWeight={600} color="#dc2626">
                          €{progressiveTotals.outbound.toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ pt: 1, borderTop: "1px solid #bfdbfe" }}>
                          <Typography variant="caption" color="#78716c">
                            {t("Balance")}
                          </Typography>
                          <Typography variant="h5" fontWeight={700} color="#1e40af">
                            €{totalBalance(progressiveTotals)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Payments Table */}
            <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb", mb: 3 }}>
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  borderBottom: "1px solid #f3f4f6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="subtitle1" fontWeight={600} color="#111827">
                    {t("Payments in Selected Period")}
                  </Typography>
                  <Chip
                    label={filteredPayments.length}
                    size="small"
                    sx={{ bgcolor: "#dbeafe", color: "#2563eb", fontWeight: 600 }}
                  />
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    size="small"
                    placeholder={t("Search...")}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      width: 220,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        fontSize: "0.875rem",
                        bgcolor: "#f9fafb",
                      },
                    }}
                  />
                  <Button
                    variant={showPayments ? "contained" : "outlined"}
                    size="small"
                    onClick={() => setShowPayments(!showPayments)}
                    endIcon={showPayments ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      ...(showPayments && {
                        bgcolor: "#ef4444",
                        "&:hover": { bgcolor: "#dc2626" },
                      }),
                    }}
                  >
                    {showPayments ? t("Hide") : t("Show")}
                  </Button>
                </Stack>
              </Box>

              <Collapse in={showPayments}>
                <Box sx={{ height: 400, width: "100%" }}>
                  <DataGrid
                    rows={filteredAndSearchedPayments}
                    columns={columns}
                    getRowId={(row) => row.id || `${row.date}-${row.entity_name}-${row.ammount}`}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    rowHeight={52}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 25 } },
                      sorting: { sortModel: [{ field: "date", sort: "desc" }] },
                    }}
                    pageSizeOptions={[10, 25, 50]}
                    sx={{
                      border: "none",
                      "& .MuiDataGrid-columnHeaders": {
                        bgcolor: "#f9fafb",
                        borderBottom: "1px solid #e5e7eb",
                      },
                      "& .MuiDataGrid-columnHeaderTitle": {
                        fontWeight: 500,
                        fontSize: "0.75rem",
                        color: "#6b7280",
                        textTransform: "uppercase",
                      },
                      "& .MuiDataGrid-cell": {
                        borderBottom: "1px solid #f3f4f6",
                      },
                    }}
                    localeText={{
                      noRowsLabel: t("No results found"),
                      MuiTablePagination: {
                        labelRowsPerPage: t("Rows per page"),
                      },
                    }}
                  />
                </Box>
              </Collapse>
            </Card>

            {/* Balances by Payment Type */}
            <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb", mb: 3 }}>
              <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #f3f4f6" }}>
                <Typography variant="subtitle1" fontWeight={600} color="#111827">
                  {t("Progressive Balances by Type")}
                </Typography>
              </Box>
              <CardContent>
                <Grid container spacing={3}>
                  {["CASH", "BANK", "CARD", "CHECK"].map((typeKey) => {
                    const data = groupedTypeTotals[typeKey];
                    if (!data) return null;
                    const colors = typeColors[typeKey];

                    return (
                      <Grid item xs={12} sm={6} lg={3} key={typeKey}>
                        <Card
                          elevation={0}
                          sx={{
                            borderRadius: 2,
                            border: "1px solid #e5e7eb",
                            height: "100%",
                          }}
                        >
                          <CardContent>
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                              <Avatar sx={{ width: 36, height: 36, bgcolor: colors.bg, color: colors.icon }}>
                                {typeIcons[typeKey]}
                              </Avatar>
                              <Typography variant="subtitle2" fontWeight={600} color="#111827">
                                {typeLabels[typeKey]}
                              </Typography>
                            </Stack>

                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" color="#6b7280" display="block">
                                {t("Before Period")}
                              </Typography>
                              <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" color="#16a34a">
                                  +€{data.before.inbound.toFixed(2)}
                                </Typography>
                                <Typography variant="body2" color="#dc2626">
                                  -€{data.before.outbound.toFixed(2)}
                                </Typography>
                              </Stack>
                              <Typography variant="body2" fontWeight={600}>
                                = €{(data.before.inbound - data.before.outbound).toFixed(2)}
                              </Typography>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" color="#6b7280" display="block">
                                {t("Period Totals")}
                              </Typography>
                              <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" color="#16a34a">
                                  +€{data.range.inbound.toFixed(2)}
                                </Typography>
                                <Typography variant="body2" color="#dc2626">
                                  -€{data.range.outbound.toFixed(2)}
                                </Typography>
                              </Stack>
                              <Typography variant="body2" fontWeight={600}>
                                = €{(data.range.inbound - data.range.outbound).toFixed(2)}
                              </Typography>
                            </Box>

                            <Box sx={{ pt: 1, borderTop: "1px solid #e5e7eb" }}>
                              <Typography variant="caption" color="#6b7280" display="block">
                                {t("Until")} {formatDate(tillDate, locale)}
                              </Typography>
                              <Typography variant="h6" fontWeight={700} color={colors.icon}>
                                €{(data.progressive.inbound - data.progressive.outbound).toFixed(2)}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>

            {/* Charts */}
            <Grid container spacing={3}>
              {/* Balance Evolution Chart */}
              <Grid item xs={12} lg={8}>
                <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb" }}>
                  <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #f3f4f6" }}>
                    <Typography variant="subtitle1" fontWeight={600} color="#111827">
                      {t("Balance Evolution")}
                    </Typography>
                  </Box>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={balanceChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} minTickGap={40} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip
                          contentStyle={{
                            borderRadius: 8,
                            border: "1px solid #e5e7eb",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                          formatter={(value) => [`€${value.toFixed(2)}`, t("Balance")]}
                        />
                        <Line
                          type="monotone"
                          dataKey="balance"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          dot={false}
                          name={t("Balance")}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Payments by Type Chart */}
              <Grid item xs={12} lg={4}>
                <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb" }}>
                  <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #f3f4f6" }}>
                    <Typography variant="subtitle1" fontWeight={600} color="#111827">
                      {t("Inbound vs Outbound by Type")}
                    </Typography>
                  </Box>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={paymentsByTypeData} layout="vertical" margin={{ left: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis type="number" tick={{ fontSize: 12 }} />
                        <YAxis dataKey="type" type="category" tick={{ fontSize: 12 }} />
                        <Tooltip
                          contentStyle={{
                            borderRadius: 8,
                            border: "1px solid #e5e7eb",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                          formatter={(value) => `€${value.toFixed(2)}`}
                        />
                        <Legend />
                        <Bar dataKey="inbound" fill="#16a34a" name={t("Inbound")} radius={[0, 4, 4, 0]} />
                        <Bar dataKey="outbound" fill="#dc2626" name={t("Outbound")} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}

        {/* Empty State */}
        {!loading && allYearPayments.length === 0 && (
          <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb", p: 6, textAlign: "center" }}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: "#f3f4f6", mx: "auto", mb: 2 }}>
              <AccountBalanceWalletIcon sx={{ fontSize: 32, color: "#9ca3af" }} />
            </Avatar>
            <Typography variant="h6" color="#374151" gutterBottom>
              {t("No payments found")}
            </Typography>
            <Typography variant="body2" color="#6b7280">
              {t("Select a date range and click View to load payment data")}
            </Typography>
          </Card>
        )}
      </Container>
    </Box>
  );
};

export default MuiCashRegister;
